import { action } from '@ember/object';
import type Owner from '@ember/owner';

import { PopularLazyLoadSelector } from 'webapp/utils/common';
import type { DuplicateCleanupConfig } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import GoalsMyBaseComponent, { type GoalsMyArgs } from 'webapp/components/goals/my/base';
import GoalsTableRowComponent from 'webapp/components/goals/table-row';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';

import type { Goal } from 'webapp/models-custom';
import FilterSortGoalMyInList from 'webapp/models-custom/filter-sort/goal-my-in-list';

export default class GoalsMyTableComponent extends GoalsMyBaseComponent {
	filterSort: FilterSortGoalMyInList;

	constructor(owner: Owner, args: GoalsMyArgs) {
		super(owner, args);

		const filterSort = new FilterSortGoalMyInList({
			selectors: PopularLazyLoadSelector.table,
			getAllItems: () => {
				return this.sortedModel;
			},
			onOpenView: (viewId: string) => {
				this.args.openView(viewId);
			},
			afterFilter: () => {
				this.doVisibleCheck();
			},
		});

		this.filterSort = filterSort;
	}

	@action
	override setupView(node: HTMLElement, sortingFavorites: boolean): void {
		super.setupView(node, sortingFavorites);

		const ctEl = $(node).find('tbody'),
			itemSelectorString = this.filterSort.itemDomSelector,
			placeholderElementFn = function (currentItem: JQuery) {
				const el = $('<tr />');
				el.addClass('drop-placeholder').append(
					'<td colspan="' +
						currentItem.find('>td:not(.toggle)').length +
						'"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></td>',
				);
				return el;
			};

		if (ctEl.length) {
			this.initSortable(ctEl, itemSelectorString, placeholderElementFn, sortingFavorites);
		}
	}

	@action
	override setupFavoritesView(node: HTMLElement): void {
		this.setupView(node, true);
	}

	@action
	override setupNonFavoritesView(node: HTMLElement): void {
		this.setupView(node, false);
	}

	get duplicateCleanupConfig(): DuplicateCleanupConfig {
		return {
			sectionSelectors: ['.favorites-section', '.non-favorites-section'],
			itemSelector: 'tr.table-row[data-id]',
			shouldKeepInSection: (itemId: string, sectionSelector: string): boolean => {
				const goal = this.mmGoalsService.goalForSpaceModels.find(
					(goal: Goal) => goal.id === itemId,
				);
				if (!goal) {
					return false;
				}
				return (
					(goal.favorite && sectionSelector === '.favorites-section') ||
					(!goal.favorite && sectionSelector === '.non-favorites-section')
				);
			},
		};
	}
	<template>
		<div class="bottom-padding sortable-table-container" {{willDestroy this.teardownView}}>
			<FilterLineProxyComponent @model={{this.filterSort}} />

			<FilterNoResultsComponent @model={{this.filterSort}} />

			{{! Favorite goals section }}
			{{#if this.hasFavoriteGoals}}
				<CommonListLazyLoadComponent
					@filterSort={{this.filterSort}}
					@filterExtraFn={{this.favoriteGoalsFilterFn}}
					@initialRowCount={{15}}
					@loadMoreRowCount={{10}}
					@animationProperty="id"
					@autoLoaded={{true}}
					@bottomDistance={{400}}
					@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
					as |visibleItems exporter|
				>
					<table
						class="goals-list flexible-table bigtable has-checkbox has-avatar favorites-section
							{{if this.filterSort.noResults 'hide'}}"
						{{! don't add dynamic classes bcos of visible-check-container }}
						{{didInsert this.setupFavoritesView}}
						{{visibleCheckContainer}}
						{{didUpdate this.triggerContentChange visibleItems}}
						{{willDestroy this.teardownView}}
					>
						<colgroup>
							<col class="cc" />
							<col class="h0" />
							<col width="25%" />
							<col width="25%" />
							<col />
							<col />
							<col />
							<col class="button-ct" />
						</colgroup>

						<thead class={{unless this.isFavoriteSectionVisible "hidden"}}>
							<tr>
								{{#if this.isFavoriteSectionVisible}}
									<CommonBatchTableHeaderComponent
										@batchSelect={{this.batchSelect}}
									/>
								{{else}}
									<th class="cc"></th>
								{{/if}}
								<th class="h0"></th>
								<th>Title</th>
								<th>Progress</th>
								<th>Timeframe</th>
								<th class="prio-low">Status</th>
								<th class="prio-low">Owner</th>
								<th class="button-ct"></th>
							</tr>
						</thead>

						{{! don"t add dynamic classes on tbody bcos of sortable }}
						<tbody>
							{{#each visibleItems as |result|}}
								<GoalsTableRowComponent
									@model={{result}}
									@batchSelect={{this.batchSelect}}
									@hideSort={{exporter.hideSort}}
								/>
							{{/each}}
						</tbody>
					</table>

					{{#unless exporter.areAllItemsLoaded}}
						<CommonListLoaderComponent />
					{{/unless}}
				</CommonListLazyLoadComponent>
			{{/if}}

			{{! Non-favorite goals section }}
			<CommonListLazyLoadComponent
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.nonFavoriteGoalsFilterFn}}
				@initialRowCount={{15}}
				@loadMoreRowCount={{10}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{400}}
				@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
				as |visibleItems exporter|
			>
				<table
					class="goals-list flexible-table bigtable has-checkbox has-avatar non-favorites-section
						{{if this.filterSort.noResults 'hide'}}"
					{{! don't add dynamic classes bcos of visible-check-container }}
					{{didInsert this.setupNonFavoritesView}}
					{{visibleCheckContainer}}
					{{didUpdate this.triggerContentChange visibleItems}}
					{{willDestroy this.teardownView}}
				>
					<colgroup>
						<col class="cc" />
						<col class="h0" />
						<col width="25%" />
						{{!-- {{#if this.showPerformanceSummary}}<col width="100">{{/if}} --}}
						<col width="25%" />
						<col />
						<col />
						<col />
						<col class="button-ct" />
					</colgroup>

					<thead class={{if this.isFavoriteSectionVisible "hidden"}}>
						<tr>
							{{#if this.isFavoriteSectionVisible}}
								<th class="cc"></th>
							{{else}}
								<CommonBatchTableHeaderComponent
									@batchSelect={{this.batchSelect}}
								/>
							{{/if}}
							<th class="h0"></th>
							<th>Title</th>
							<th>Progress</th>
							<th>Timeframe</th>
							<th class="prio-low">Status</th>
							<th class="prio-low">Owner</th>
							<th class="button-ct"></th>
						</tr>
					</thead>

					{{! don"t add dynamic classes on tbody bcos of sortable }}
					<tbody>
						{{#each visibleItems as |result|}}
							<GoalsTableRowComponent
								@model={{result}}
								@batchSelect={{this.batchSelect}}
								@hideSort={{exporter.hideSort}}
							/>
						{{/each}}
					</tbody>
				</table>

				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>

			<TableAddNewItemComponent
				@title={{this.addButtonText}}
				@addNewItem={{@onAdd}}
				@model={{this.filterSort}}
			/>
		</div>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent @text={{this.addButtonText}} @callTrigger={{@onAdd}} />
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
